import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'

// ---- Types ----

export interface SimulationScenario {
  id: string
  name: string
  agent_name: string
  prompt: Record<string, any>
  service_mode: string
  success_criteria: SuccessCriterion[]
  tags: string[]
  created_at: string
  updated_at: string
}

export interface SuccessCriterion {
  field: string
  operator: 'eq' | 'neq' | 'contains' | 'gt' | 'lt' | 'gte' | 'lte' | 'exists' | 'regex'
  value: any
  description?: string
}

export interface SimulationRun {
  id: string
  scenario_id?: string
  agent_name: string
  status: 'pending' | 'running' | 'passed' | 'failed' | 'error'
  score: number | null
  duration_ms: number | null
  created_at: string
  completed_at: string | null
  error_message?: string
}

export interface SimulationTrace {
  run_id: string
  nodes: TraceNode[]
  total_duration_ms: number
}

export interface TraceNode {
  name: string
  status: 'completed' | 'failed' | 'skipped'
  duration_ms: number
  llm_calls: number
  tool_calls: number
  input?: Record<string, any>
  output?: Record<string, any>
  llm_details?: any[]
  tool_details?: any[]
}

export interface SimulationEvaluation {
  run_id: string
  overall_passed: boolean
  criteria_results: CriterionResult[]
}

export interface CriterionResult {
  criterion: string
  passed: boolean
  detail: string
}

export interface SimulationSuite {
  id: string
  name: string
  scenario_ids: string[]
  created_at: string
}

export interface ReplayData {
  run_id: string
  steps: ReplayStep[]
  total_steps: number
}

export interface ReplayStep {
  index: number
  node_name: string
  status: string
  duration_ms: number
  input_state: Record<string, any>
  output_state: Record<string, any>
  llm_calls: any[]
  tool_calls: any[]
}

export interface CompareResult {
  run_a: SimulationRun
  run_b: SimulationRun
  trace_diff: any
  evaluation_diff: any
  telemetry_diff: any
}

export interface DashboardOverview {
  total_runs: number
  pass_rate: number
  total_scenarios: number
  avg_latency_ms: number
  recent_runs: SimulationRun[]
  runs_by_agent: Record<string, number>
}

export interface AnalyticsSnapshot {
  overall_pass_rate: number
  pass_rate_trend: Array<{ date: string; pass_rate: number }>
  per_agent_pass_rate: Array<{ agent: string; pass_rate: number; total: number }>
  top_failing_scenarios: Array<{ scenario_id: string; scenario_name: string; fail_count: number; last_failed: string }>
  cost_summary: {
    total_cost: number
    total_tokens: number
    avg_cost_per_run: number
    avg_tokens_per_run: number
  }
}

export interface ShadowStats {
  total_captures: number
  agents: string[]
  date_range: { from: string; to: string }
}

// ---- Helpers ----

const BASE = '/api/v1/simulation'

async function simGet<T>(path: string, params?: Record<string, any>): Promise<T> {
  // Use the underlying axios instance via apiClient's exposed client
  // We call apiClient methods following the same pattern as the rest of the codebase
  const resp = await (apiClient as any).client.get(`${BASE}${path}`, { params })
  return resp.data
}

async function simPost<T>(path: string, data?: any): Promise<T> {
  const resp = await (apiClient as any).client.post(`${BASE}${path}`, data)
  return resp.data
}

async function simPut<T>(path: string, data?: any): Promise<T> {
  const resp = await (apiClient as any).client.put(`${BASE}${path}`, data)
  return resp.data
}

async function simDelete<T>(path: string): Promise<T> {
  const resp = await (apiClient as any).client.delete(`${BASE}${path}`)
  return resp.data
}

// ---- Store ----

class SimulationStore {
  // Dashboard
  overview: DashboardOverview | null = null
  overviewLoading = false
  overviewError: string | null = null

  // Scenarios
  scenarios: SimulationScenario[] = []
  scenariosLoading = false
  scenariosError: string | null = null
  selectedScenario: SimulationScenario | null = null

  // Runs
  runs: SimulationRun[] = []
  runsLoading = false
  runsError: string | null = null
  selectedRun: SimulationRun | null = null

  // Trace & Evaluation
  currentTrace: SimulationTrace | null = null
  traceLoading = false
  currentEvaluation: SimulationEvaluation | null = null
  evaluationLoading = false

  // Suites
  suites: SimulationSuite[] = []
  suitesLoading = false

  // Replay
  replayData: ReplayData | null = null
  replayLoading = false
  currentStepIndex = 0

  // Compare
  compareResult: CompareResult | null = null
  compareLoading = false

  // Analytics
  analyticsSnapshot: AnalyticsSnapshot | null = null
  analyticsLoading = false
  analyticsError: string | null = null

  // Shadow
  shadowStats: ShadowStats | null = null
  shadowCaptures: any[] = []

  // Filters
  agentFilter = ''
  tagFilter = ''

  constructor() {
    makeAutoObservable(this)
  }

  // ---- Computed ----

  get filteredScenarios(): SimulationScenario[] {
    return this.scenarios.filter((s) => {
      const matchesAgent = !this.agentFilter || s.agent_name === this.agentFilter
      const matchesTag = !this.tagFilter || s.tags.includes(this.tagFilter)
      return matchesAgent && matchesTag
    })
  }

  get allAgentNames(): string[] {
    const names = new Set(this.scenarios.map((s) => s.agent_name))
    return Array.from(names).sort()
  }

  get allTags(): string[] {
    const tags = new Set(this.scenarios.flatMap((s) => s.tags))
    return Array.from(tags).sort()
  }

  get currentStep(): ReplayStep | null {
    if (!this.replayData) return null
    return this.replayData.steps[this.currentStepIndex] ?? null
  }

  // ---- Dashboard ----

  async fetchOverview() {
    this.overviewLoading = true
    this.overviewError = null
    try {
      const data = await simGet<DashboardOverview>('/dashboard/overview')
      runInAction(() => {
        this.overview = data
        this.overviewLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.overviewError = error.response?.data?.detail || 'Failed to fetch dashboard'
        this.overviewLoading = false
      })
    }
  }

  // ---- Scenarios CRUD ----

  async fetchScenarios() {
    this.scenariosLoading = true
    this.scenariosError = null
    try {
      const data = await simGet<SimulationScenario[]>('/scenarios')
      runInAction(() => {
        this.scenarios = data
        this.scenariosLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.scenarios = []
        this.scenariosError = error.response?.data?.detail || 'Failed to fetch scenarios'
        this.scenariosLoading = false
      })
    }
  }

  async fetchScenarioById(id: string) {
    this.scenariosLoading = true
    try {
      const data = await simGet<SimulationScenario>(`/scenarios/${id}`)
      runInAction(() => {
        this.selectedScenario = data
        this.scenariosLoading = false
      })
      return data
    } catch (error: any) {
      runInAction(() => {
        this.scenariosError = error.response?.data?.detail || 'Failed to fetch scenario'
        this.scenariosLoading = false
      })
      throw error
    }
  }

  async createScenario(data: Omit<SimulationScenario, 'id' | 'created_at' | 'updated_at'>) {
    this.scenariosLoading = true
    try {
      const scenario = await simPost<SimulationScenario>('/scenarios', data)
      runInAction(() => {
        this.scenarios = [scenario, ...this.scenarios]
        this.scenariosLoading = false
      })
      return scenario
    } catch (error: any) {
      runInAction(() => {
        this.scenariosError = error.response?.data?.detail || 'Failed to create scenario'
        this.scenariosLoading = false
      })
      throw error
    }
  }

  async updateScenario(id: string, data: Partial<SimulationScenario>) {
    this.scenariosLoading = true
    try {
      const scenario = await simPut<SimulationScenario>(`/scenarios/${id}`, data)
      runInAction(() => {
        this.scenarios = this.scenarios.map((s) => (s.id === id ? scenario : s))
        if (this.selectedScenario?.id === id) this.selectedScenario = scenario
        this.scenariosLoading = false
      })
      return scenario
    } catch (error: any) {
      runInAction(() => {
        this.scenariosError = error.response?.data?.detail || 'Failed to update scenario'
        this.scenariosLoading = false
      })
      throw error
    }
  }

  async deleteScenario(id: string) {
    this.scenariosLoading = true
    try {
      await simDelete(`/scenarios/${id}`)
      runInAction(() => {
        this.scenarios = this.scenarios.filter((s) => s.id !== id)
        if (this.selectedScenario?.id === id) this.selectedScenario = null
        this.scenariosLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.scenariosError = error.response?.data?.detail || 'Failed to delete scenario'
        this.scenariosLoading = false
      })
      throw error
    }
  }

  // ---- Runs ----

  async fetchRuns() {
    this.runsLoading = true
    this.runsError = null
    try {
      const data = await simGet<SimulationRun[]>('/runs')
      runInAction(() => {
        this.runs = data
        this.runsLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.runs = []
        this.runsError = error.response?.data?.detail || 'Failed to fetch runs'
        this.runsLoading = false
      })
    }
  }

  async fetchRunById(id: string) {
    this.runsLoading = true
    try {
      const data = await simGet<SimulationRun>(`/runs/${id}`)
      runInAction(() => {
        this.selectedRun = data
        this.runsLoading = false
      })
      return data
    } catch (error: any) {
      runInAction(() => {
        this.runsError = error.response?.data?.detail || 'Failed to fetch run'
        this.runsLoading = false
      })
      throw error
    }
  }

  async runFromScenario(scenarioId: string) {
    this.runsLoading = true
    try {
      const run = await simPost<SimulationRun>(`/scenarios/${scenarioId}/run`)
      runInAction(() => {
        this.runs = [run, ...this.runs]
        this.runsLoading = false
      })
      return run
    } catch (error: any) {
      runInAction(() => {
        this.runsError = error.response?.data?.detail || 'Failed to run scenario'
        this.runsLoading = false
      })
      throw error
    }
  }

  async runAdHoc(agentName: string, prompt: string) {
    this.runsLoading = true
    try {
      const run = await simPost<SimulationRun>('/runs/ad-hoc', { agent_name: agentName, prompt })
      runInAction(() => {
        this.runs = [run, ...this.runs]
        this.runsLoading = false
      })
      return run
    } catch (error: any) {
      runInAction(() => {
        this.runsError = error.response?.data?.detail || 'Failed to run ad-hoc simulation'
        this.runsLoading = false
      })
      throw error
    }
  }

  // ---- Trace & Evaluation ----

  async fetchTrace(runId: string) {
    this.traceLoading = true
    try {
      const data = await simGet<SimulationTrace>(`/runs/${runId}/trace`)
      runInAction(() => {
        this.currentTrace = data
        this.traceLoading = false
      })
      return data
    } catch (error: any) {
      runInAction(() => {
        this.traceLoading = false
      })
      throw error
    }
  }

  async fetchEvaluation(runId: string) {
    this.evaluationLoading = true
    try {
      const data = await simGet<SimulationEvaluation>(`/runs/${runId}/evaluation`)
      runInAction(() => {
        this.currentEvaluation = data
        this.evaluationLoading = false
      })
      return data
    } catch (error: any) {
      runInAction(() => {
        this.evaluationLoading = false
      })
      throw error
    }
  }

  // ---- Suites ----

  async fetchSuites() {
    this.suitesLoading = true
    try {
      const data = await simGet<SimulationSuite[]>('/suites')
      runInAction(() => {
        this.suites = data
        this.suitesLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.suitesLoading = false
      })
    }
  }

  async createSuite(name: string, scenarioIds: string[]) {
    this.suitesLoading = true
    try {
      const suite = await simPost<SimulationSuite>('/suites', { name, scenario_ids: scenarioIds })
      runInAction(() => {
        this.suites = [suite, ...this.suites]
        this.suitesLoading = false
      })
      return suite
    } catch (error: any) {
      runInAction(() => {
        this.suitesLoading = false
      })
      throw error
    }
  }

  async runSuite(suiteId: string) {
    this.runsLoading = true
    try {
      const runs = await simPost<SimulationRun[]>(`/suites/${suiteId}/run`)
      runInAction(() => {
        this.runs = [...runs, ...this.runs]
        this.runsLoading = false
      })
      return runs
    } catch (error: any) {
      runInAction(() => {
        this.runsLoading = false
      })
      throw error
    }
  }

  // ---- Replay ----

  async loadReplay(runId: string) {
    this.replayLoading = true
    this.currentStepIndex = 0
    try {
      const data = await simGet<ReplayData>(`/runs/${runId}/replay`)
      runInAction(() => {
        this.replayData = data
        this.replayLoading = false
      })
      return data
    } catch (error: any) {
      runInAction(() => {
        this.replayLoading = false
      })
      throw error
    }
  }

  async fetchReplayNodeState(runId: string, stepIndex: number) {
    try {
      const data = await simGet<ReplayStep>(`/runs/${runId}/replay/${stepIndex}`)
      return data
    } catch (error: any) {
      throw error
    }
  }

  setReplayStep(index: number) {
    if (this.replayData && index >= 0 && index < this.replayData.total_steps) {
      this.currentStepIndex = index
    }
  }

  stepForward() {
    if (this.replayData && this.currentStepIndex < this.replayData.total_steps - 1) {
      this.currentStepIndex++
    }
  }

  stepBack() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--
    }
  }

  // ---- Compare ----

  async compareTwoRuns(runIdA: string, runIdB: string) {
    this.compareLoading = true
    try {
      const data = await simPost<CompareResult>('/compare', { run_id_a: runIdA, run_id_b: runIdB })
      runInAction(() => {
        this.compareResult = data
        this.compareLoading = false
      })
      return data
    } catch (error: any) {
      runInAction(() => {
        this.compareLoading = false
      })
      throw error
    }
  }

  // ---- Seed ----

  async seedExploration(agentName: string) {
    try {
      const data = await simPost<{ scenarios_created: number }>('/seed/exploration', { agent_name: agentName })
      // Refresh scenarios after seeding
      await this.fetchScenarios()
      return data
    } catch (error: any) {
      throw error
    }
  }

  async seedAllAgents() {
    try {
      const data = await simPost<{ total_scenarios_created: number }>('/seed/all-agents')
      await this.fetchScenarios()
      return data
    } catch (error: any) {
      throw error
    }
  }

  // ---- Analytics ----

  async fetchAnalyticsSnapshot() {
    this.analyticsLoading = true
    this.analyticsError = null
    try {
      // Use the dashboard overview endpoint (ground truth with real data)
      const overview = await simGet<DashboardOverview>('/dashboard/overview')
      const runsPerAgent = overview.runs_by_agent || {}

      // Transform into AnalyticsSnapshot format
      const snapshot: AnalyticsSnapshot = {
        overall_pass_rate: (overview.pass_rate || 0) * 100,
        pass_rate_trend: [],
        per_agent_pass_rate: Object.entries(runsPerAgent).map(([agent, count]) => ({
          agent,
          pass_rate: (overview.pass_rate || 0) * 100,
          total: count,
        })),
        top_failing_scenarios: [],
        cost_summary: {
          total_cost: 0,
          total_tokens: 0,
          avg_cost_per_run: 0,
          avg_tokens_per_run: 0,
        },
      }

      runInAction(() => {
        this.analyticsSnapshot = snapshot
        this.analyticsLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.analyticsError = error.response?.data?.detail || 'Failed to fetch analytics'
        this.analyticsLoading = false
      })
    }
  }

  // ---- Shadow ----

  async fetchShadowStats() {
    try {
      const data = await simGet<ShadowStats>('/shadow/stats')
      runInAction(() => {
        this.shadowStats = data
      })
    } catch {
      // non-critical
    }
  }

  async fetchShadowCaptures(params?: { agent_name?: string; limit?: number }) {
    try {
      const data = await simGet<any[]>('/shadow/captures', params)
      runInAction(() => {
        this.shadowCaptures = data
      })
    } catch {
      // non-critical
    }
  }

  // ---- Filter actions ----

  setAgentFilter(agent: string) {
    this.agentFilter = agent
  }

  setTagFilter(tag: string) {
    this.tagFilter = tag
  }

  clearFilters() {
    this.agentFilter = ''
    this.tagFilter = ''
  }

  // ---- Utility ----

  clearError() {
    this.overviewError = null
    this.scenariosError = null
    this.runsError = null
    this.analyticsError = null
  }

  clearSelectedRun() {
    this.selectedRun = null
    this.currentTrace = null
    this.currentEvaluation = null
  }

  clearReplay() {
    this.replayData = null
    this.currentStepIndex = 0
  }
}

export const simulationStore = new SimulationStore()
